.hero {
  position: relative;
  display: flex;
  align-items: center;
  /* Fills the viewport minus the header, without fighting mobile browser chrome. */
  min-height: min(46rem, calc(100dvh - 4.25rem));
  padding-block: clamp(3.5rem, 10vh, 7rem);
  overflow: hidden;
}

/* Soft accent wash behind the type. Purely decorative. */
.glow {
  position: absolute;
  inset: -30% -10% auto -10%;
  height: 70vh;
  pointer-events: none;
  background:
    radial-gradient(45% 55% at 18% 30%, var(--accent-quiet), transparent 70%),
    radial-gradient(40% 50% at 82% 12%, color-mix(in srgb, var(--accent) 8%, transparent), transparent 70%);
}

.inner {
  position: relative;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1.5rem;
}

.pulse {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 0 var(--accent);
  animation: pulse 2.6s ease-out infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 55%, transparent);
  }
  70%,
  100% {
    box-shadow: 0 0 0 0.6rem transparent;
  }
}

.name {
  font-family: var(--font-display);
  font-size: var(--step-4);
  letter-spacing: -0.02em;
  margin-bottom: 0.35rem;
}

/* --- Role rotator --------------------------------------------------------- */

.roles {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  font-size: var(--step-2);
  color: var(--text-dim);
  margin-bottom: 1.5rem;
}

.rolesPrefix {
  color: var(--text-faint);
}

/*
  A fixed-size stage: every role is stacked in the same grid cell, so the line
  never reflows as the words change. `1fr` rows keep the tallest one in charge.
*/
.rotator {
  display: inline-grid;
  grid-template-areas: 'role';
}

.role {
  grid-area: role;
  color: var(--accent);
  font-weight: 600;
  white-space: nowrap;
  opacity: 0;
  transform: translateY(0.4em);
  transition: opacity 400ms ease, transform 400ms ease;
}

.role[data-visible='true'] {
  opacity: 1;
  transform: translateY(0);
}

.tagline {
  max-width: 46ch;
  font-size: var(--step-1);
  color: var(--text-dim);
  margin-bottom: 2.25rem;
}

/* --- Calls to action ------------------------------------------------------ */

.ctas {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 3.25rem;
}

.primary,
.secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.35rem;
  border-radius: var(--radius-pill);
  font-size: var(--step--1);
  font-weight: 600;
  text-decoration: none;
  transition: transform 150ms ease, background-color 150ms ease, border-color 150ms ease,
    color 150ms ease;
}

.primary {
  background: var(--accent);
  color: var(--on-accent);
  border: 1px solid var(--accent);
}

.primary:hover {
  color: var(--on-accent);
  background: var(--accent-strong);
  border-color: var(--accent-strong);
  transform: translateY(-2px);
}

.secondary {
  border: 1px solid var(--border-strong);
  color: var(--text);
  background: transparent;
}

.secondary:hover {
  color: var(--text);
  border-color: var(--accent);
  background: var(--bg-elev-2);
  transform: translateY(-2px);
}

/* --- Fact strip ----------------------------------------------------------- */

.facts {
  display: flex;
  flex-wrap: wrap;
  gap: 0 clamp(1.5rem, 5vw, 3.5rem);
  padding-top: 1.75rem;
  border-top: 1px solid var(--border);
  margin: 0;
}

.fact dt {
  font-family: var(--font-mono);
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-faint);
}

.fact dd {
  margin: 0.15rem 0 0;
  font-size: var(--step-1);
  font-weight: 600;
  color: var(--text);
}

/* --- Scroll cue ----------------------------------------------------------- */

.scrollCue {
  display: none;
  align-items: center;
  gap: 0.45rem;
  margin-top: 2.5rem;
  color: var(--text-faint);
  font-family: var(--font-mono);
  font-size: 0.75rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
}

.scrollCue:hover {
  color: var(--accent);
}

.scrollCue svg {
  animation: nudge 2.2s ease-in-out infinite;
}

@keyframes nudge {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(3px);
  }
}

/* Only worth showing when there is room below the fold. */
@media (min-height: 44rem) {
  .scrollCue {
    display: inline-flex;
  }
}
